import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { Redirect } from "react-router-dom";

import RedzoneStatsDisplay from "./RedzoneStatsDisplay";
import RedzoneTeamSplitsDisplay from "./RedzoneTeamSplitsDisplay";
import NFLToolLayout, { SeasonSelect } from "../__shared/NFLToolLayout";
import FilterBar from "../__shared/FilterBar";
import TeamFilter from "../__shared/TeamFilter";
import ToolStatePanel, { TableSkeleton } from "../__shared/ToolStatePanel";
import ToolErrorBoundary from "../__shared/ToolErrorBoundary";
import useNflReference from "../__shared/useNflReference";
import useUrlFilters from "../__shared/useUrlFilters";
import useDebouncedValue from "../__shared/useDebouncedValue";
import { resolveTeamIds, seasonFor } from "../__shared/nflToolFilters";
import { isToolSub } from "../../../../../AuthUtils";
import "./NFLRedzoneAdvantagePage.css";

const REDZONE_PASSING = graphql`
  query NFLRedzoneAdvantagePagePassingQuery(
    $teamIds: [ID]
    $minAtt: Int
    $seasonID: ID
  ) {
    nflRedzonePassingStats(
      teamIds: $teamIds
      minAtt: $minAtt
      seasonId: $seasonID
    ) {
      player {
        firstName
        lastName
        position {
          name
        }
        team {
          logoSrc
        }
      }

      attempts
      interceptions
      completions
      touchdowns
    }
  }
`;

const REDZONE_RUSHING = graphql`
  query NFLRedzoneAdvantagePageRushingQuery(
    $teamIds: [ID]
    $minRsh: Int
    $seasonID: ID
  ) {
    nflRedzoneRushingStats(
      teamIds: $teamIds
      minRsh: $minRsh
      seasonId: $seasonID
    ) {
      player {
        firstName
        lastName
        position {
          name
        }
        team {
          logoSrc
        }
      }

      rushes
      fumbles
      touchdowns
    }
  }
`;

const REDZONE_RECEIVING = graphql`
  query NFLRedzoneAdvantagePageReceivingQuery(
    $teamIds: [ID]
    $minTgt: Int
    $seasonID: ID
  ) {
    nflRedzoneReceivingStats(
      teamIds: $teamIds
      minTgt: $minTgt
      seasonId: $seasonID
    ) {
      player {
        firstName
        lastName
        position {
          name
        }
        team {
          logoSrc
        }
      }

      targets
      receptions
      touchdowns
    }
  }
`;

const FILTER_OPTIONS = { positions: [] };
const TITLE = "Redzone";
const SUBTITLE = "Passing, rushing and receiving inside the 20.";

const PASSING_COLUMNS = [
  { Header: "Att", accessor: "attempts" },
  { Header: "Comp", accessor: "completions" },
  { Header: "Int", accessor: "interceptions" },
  { Header: "TDs", accessor: "touchdowns" },
];

const RUSHING_COLUMNS = [
  { Header: "Rsh", accessor: "rushes" },
  { Header: "Fum", accessor: "fumbles" },
  { Header: "TDs", accessor: "touchdowns" },
];

const RECEIVING_COLUMNS = [
  { Header: "Tar", accessor: "targets" },
  { Header: "Rec", accessor: "receptions" },
  { Header: "TDs", accessor: "touchdowns" },
];

const SKELETON_COLUMNS = ["Player", "Att", "Comp", "Int", "TDs"];

export default function NFLRedzoneAdvantagePage() {
  const [referenceFetchKey, setReferenceFetchKey] = useState(0);

  return (
    <ToolErrorBoundary
      what="Redzone stats"
      onRetry={() => setReferenceFetchKey((key) => key + 1)}
      wrapError={(panel) => (
        <NFLToolLayout tool="redzone" title={TITLE} subtitle={SUBTITLE}>
          {panel}
        </NFLToolLayout>
      )}
    >
      <React.Suspense
        fallback={
          <NFLToolLayout tool="redzone" title={TITLE} subtitle={SUBTITLE}>
            <TableSkeleton columns={SKELETON_COLUMNS} />
          </NFLToolLayout>
        }
      >
        <RedzoneTool referenceFetchKey={referenceFetchKey} />
      </React.Suspense>
    </ToolErrorBoundary>
  );
}

function SplitsSkeleton() {
  return (
    <div
      className="nfl-rz-splits-skeleton"
      role="status"
      aria-busy="true"
      aria-label="Loading team splits"
    >
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <span key={row} className="nfl-shimmer nfl-rz-split-shimmer" />
      ))}
    </div>
  );
}

function RedzoneTool({ referenceFetchKey }) {
  const reference = useNflReference({ fetchKey: referenceFetchKey });
  const filters = useUrlFilters(FILTER_OPTIONS);
  const [fetchKey, setFetchKey] = useState(0);

  const season = seasonFor(filters.season, reference);

  const teamByAbbrev = useMemo(() => {
    const map = {};
    reference.teams.forEach((team) => {
      map[team.abbreviation] = team;
    });
    return map;
  }, [reference.teams]);

  const knownTeams = useMemo(
    () => filters.teams.filter((abbrev) => teamByAbbrev[abbrev]),
    [filters.teams, teamByAbbrev]
  );

  const teamIds = useMemo(
    () => resolveTeamIds(knownTeams, reference.teams),
    [knownTeams, reference.teams]
  );

  const combinedKey = `${season ? season.id : ""}|${teamIds.join(",")}`;
  const debouncedKey = useDebouncedValue(combinedKey, 400);

  const variables = useMemo(() => {
    const pipeIndex = debouncedKey.indexOf("|");
    const seasonID = debouncedKey.slice(0, pipeIndex);
    const teamsPart = debouncedKey.slice(pipeIndex + 1);
    const ids = teamsPart ? teamsPart.split(",") : [];
    const min = ids.length === 0 ? 1 : undefined;
    return { seasonID: seasonID || null, teamIds: ids, min };
  }, [debouncedKey]);

  const deferredVariables = useDeferredValue(variables);
  const isUpdating =
    combinedKey !== debouncedKey || variables !== deferredVariables;

  const retry = useCallback(() => setFetchKey((key) => key + 1), []);

  if (!isToolSub(reference.groups)) {
    return <Redirect to="/memberships" />;
  }

  const chips = knownTeams.map((abbrev) => {
    const team = teamByAbbrev[abbrev];
    return {
      key: `team-${abbrev}`,
      label: team.name,
      color: team.primaryColorHex,
      onRemove: () => filters.toggleTeam(abbrev),
    };
  });

  const clearAll = chips.length > 0 ? () => filters.clear("teams") : undefined;
  const lastChip = chips[chips.length - 1];
  const accentColor =
    knownTeams.length === 1 ? teamByAbbrev[knownTeams[0]].primaryColorHex : undefined;
  const minLabel = deferredVariables.min === 1 ? "Min 1" : null;
  const { seasonID, teamIds: queryTeamIds, min } = deferredVariables;

  const buildEmptyState = (
    noFilterTitle,
    noFilterBody,
    filteredTitle = "No players match these filters"
  ) =>
    chips.length > 0 ? (
      <ToolStatePanel
        title={filteredTitle}
        body={chips.map((chip) => chip.label).join(" · ")}
        actions={
          <>
            <button type="button" className="nfl-btn" onClick={lastChip.onRemove}>
              Remove {lastChip.label}
            </button>
            <button type="button" className="nfl-btn nfl-btn-ghost" onClick={clearAll}>
              Clear all filters
            </button>
          </>
        }
      />
    ) : (
      <ToolStatePanel title={noFilterTitle} body={noFilterBody} />
    );

  const NO_FILTER_BODY = "Red-zone plays show up here after games are played.";
  const passingEmptyState = buildEmptyState("No passing yet", NO_FILTER_BODY);
  const rushingEmptyState = buildEmptyState("No rushing yet", NO_FILTER_BODY);
  const receivingEmptyState = buildEmptyState("No receiving yet", NO_FILTER_BODY);
  const splitsEmptyState = buildEmptyState(
    "No redzone splits yet",
    "Splits show up here once red-zone plays are logged.",
    "No teams match these filters"
  );

  return (
    <NFLToolLayout
      tool="redzone"
      title={TITLE}
      subtitle={SUBTITLE}
      accentColor={accentColor}
      actions={
        <SeasonSelect
          seasons={reference.seasons}
          value={season ? season.year : ""}
          onChange={(year) =>
            filters.setSeason(
              reference.currentSeason && year === reference.currentSeason.year
                ? null
                : year
            )
          }
        />
      }
      filters={
        <FilterBar
          primary={
            <TeamFilter
              teams={reference.teams}
              selected={knownTeams}
              onToggle={filters.toggleTeam}
            />
          }
          chips={chips}
          onClearAll={clearAll}
          isUpdating={isUpdating}
        />
      }
    >
      <div className={isUpdating ? "nfl-is-updating" : undefined}>
        <div className="nfl-rz-groups">
          <RedzoneStatsDisplay
            title="Passing"
            query={REDZONE_PASSING}
            dataKey="nflRedzonePassingStats"
            statColumns={PASSING_COLUMNS}
            variables={{ seasonID, teamIds: queryTeamIds, minAtt: min }}
            fetchKey={fetchKey}
            onRetry={retry}
            minLabel={minLabel}
            emptyState={passingEmptyState}
          />
          <RedzoneStatsDisplay
            title="Rushing"
            query={REDZONE_RUSHING}
            dataKey="nflRedzoneRushingStats"
            statColumns={RUSHING_COLUMNS}
            variables={{ seasonID, teamIds: queryTeamIds, minRsh: min }}
            fetchKey={fetchKey}
            onRetry={retry}
            minLabel={minLabel}
            emptyState={rushingEmptyState}
          />
          <RedzoneStatsDisplay
            title="Receiving"
            query={REDZONE_RECEIVING}
            dataKey="nflRedzoneReceivingStats"
            statColumns={RECEIVING_COLUMNS}
            variables={{ seasonID, teamIds: queryTeamIds, minTgt: min }}
            fetchKey={fetchKey}
            onRetry={retry}
            minLabel={minLabel}
            emptyState={receivingEmptyState}
          />
        </div>

        <section className="nfl-rz-splits-section">
          <div className="nfl-rz-group-head">
            <h2 className="nfl-rz-group-title nfl-display">Team redzone splits</h2>
          </div>
          <ToolErrorBoundary what="Team splits" onRetry={retry}>
            <React.Suspense fallback={<SplitsSkeleton />}>
              <RedzoneTeamSplitsDisplay
                variables={{ seasonID, teamIds: queryTeamIds }}
                fetchKey={fetchKey}
                emptyState={splitsEmptyState}
              />
            </React.Suspense>
          </ToolErrorBoundary>
        </section>
      </div>
    </NFLToolLayout>
  );
}
